import React from "react";
import styles from "../../styles/pages/static/PrivacyPolicyPage.module.css";
import PageSeo from "../../components/common/PageSeo";

const PrivacyPolicyPage = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const lastUpdated = "September 10, 2026";

  return (
    <div className="pageContainer">
      <PageSeo
        title="Privacy Policy"
        description="Read Simpary's privacy policy."
        pathname="/privacy"
      />
      <div className="innerContainer">
        <div className={styles.privacyPage}>
        {/* Header Section */}
        <section className={styles.headerSection}>
          <div className={styles.headerContent}>
            <h1 className={styles.pageTitle}>Privacy Policy</h1>
            <p className={styles.lastUpdated}>Last Updated: {lastUpdated}</p>
          </div>
        </section>

        {/* Content Section */}
        <section className={styles.contentSection}>
          <div className={styles.container}>
            <div className={styles.contentGrid}>
              {/* Table of Contents */}
              <aside className={styles.tableOfContents}>
                <h3 className={styles.tocTitle}>Table of Contents</h3>
                <nav className={styles.tocNav}>
                  <a href="#introduction" onClick={() => scrollToSection("introduction")} className={styles.tocLink}>
                    1. Introduction
                  </a>
                  <a href="#information-collection" onClick={() => scrollToSection("information-collection")} className={styles.tocLink}>
                    2. Information We Collect
                  </a>
                  <a href="#how-we-use" onClick={() => scrollToSection("how-we-use")} className={styles.tocLink}>
                    3. How We Use Information
                  </a>
                  <a href="#information-sharing" onClick={() => scrollToSection("information-sharing")} className={styles.tocLink}>
                    4. Information Sharing
                  </a>
                  <a href="#data-security" onClick={() => scrollToSection("data-security")} className={styles.tocLink}>
                    5. Data Security
                  </a>
                  <a href="#user-rights" onClick={() => scrollToSection("user-rights")} className={styles.tocLink}>
                    6. Your Rights
                  </a>
                  <a href="#cookies" onClick={() => scrollToSection("cookies")} className={styles.tocLink}>
                    7. Cookies & Tracking
                  </a>
                  <a href="#third-party" onClick={() => scrollToSection("third-party")} className={styles.tocLink}>
                    8. Third-Party Services
                  </a>
                  <a href="#childrens-privacy" onClick={() => scrollToSection("childrens-privacy")} className={styles.tocLink}>
                    9. Children's Privacy
                  </a>
                  <a href="#policy-updates" onClick={() => scrollToSection("policy-updates")} className={styles.tocLink}>
                    10. Policy Updates
                  </a>
                  <a href="#contact" onClick={() => scrollToSection("contact")} className={styles.tocLink}>
                    11. Contact Us
                  </a>
                </nav>
              </aside>

              {/* Main Content */}
              <div className={styles.mainContent}>
                {/* Introduction */}
                <section id="introduction" className={styles.section}>
                  <h2 className={styles.sectionTitle}>1. Introduction</h2>
                  <p className={styles.paragraph}>
                    Welcome to Simpary. We are committed to protecting your 
                    privacy and ensuring the security of your personal information. This Privacy Policy 
                    explains how we collect, use, disclose, and safeguard your information when you 
                    use our electronics comparison platform.
                  </p>
                  <p className={styles.paragraph}>
                    By accessing or using Simpary, you agree to the terms outlined in this Privacy Policy. 
                    If you do not agree with our policies and practices, please do not use our services.
                  </p>
                </section>

                {/* Information Collection */}
                <section id="information-collection" className={styles.section}>
                  <h2 className={styles.sectionTitle}>2. Information We Collect</h2>
                  
                  <h3 className={styles.subsectionTitle}>2.1 Information You Provide</h3>
                  <p className={styles.paragraph}>
                    We collect information that you voluntarily provide to us, including:
                  </p>
                  <ul className={styles.list}>
                    <li>Account registration information (name, email address, password)</li>
                    <li>Profile information (username, bio, preferences)</li>
                    <li>Product reviews, ratings, and comments</li>
                    <li>Comparison lists and saved products</li>
                    <li>Messages and communications sent through our platform</li>
                    <li>Contact form submissions and support requests</li>
                  </ul>

                  <h3 className={styles.subsectionTitle}>2.2 Automatically Collected Information</h3>
                  <p className={styles.paragraph}>
                    When you use our services, we automatically collect certain information:
                  </p>
                  <ul className={styles.list}>
                    <li>Device information (IP address, browser type, operating system)</li>
                    <li>Usage data (pages viewed, time spent, navigation patterns)</li>
                    <li>Location data (approximate location based on IP address)</li>
                    <li>Cookies and similar tracking technologies</li>
                  </ul>

                  <h3 className={styles.subsectionTitle}>2.3 Information from Third Parties</h3>
                  <p className={styles.paragraph}>
                    We may receive information about you from third-party sources, including:
                  </p>
                  <ul className={styles.list}>
                    <li>Social media platforms (if you connect your account)</li>
                    <li>Analytics providers</li>
                    <li>Marketing partners</li>
                  </ul>
                </section>

                {/* How We Use Information */}
                <section id="how-we-use" className={styles.section}>
                  <h2 className={styles.sectionTitle}>3. How We Use Your Information</h2>
                  <p className={styles.paragraph}>
                    We use the collected information for the following purposes:
                  </p>
                  <ul className={styles.list}>
                    <li>To provide, maintain, and improve our services</li>
                    <li>To personalize your experience and provide tailored recommendations</li>
                    <li>To process your transactions and manage your account</li>
                    <li>To communicate with you about updates, promotions, and service changes</li>
                    <li>To respond to your inquiries and provide customer support</li>
                    <li>To analyze usage patterns and optimize our platform</li>
                    <li>To detect, prevent, and address technical issues and fraud</li>
                    <li>To comply with legal obligations and enforce our terms</li>
                  </ul>

                  <h3 className={styles.subsectionTitle}>3.1 Training Our Product-Matching Engine</h3>
                  <p className={styles.paragraph}>
                    We use your wizard answers, search interactions (views, clicks, comparisons,
                    favorites), and outcomes (such as which product you ultimately chose) to train
                    and evaluate the recommendation system that produces your Fit score. Before this
                    data is used for training, it is linked to a one-way, non-reversible identifier
                    rather than your account ID or name, so the training data itself cannot be used
                    to re-identify you. Training records are retained for a limited period
                    (currently up to 180 days) and are permanently deleted, along with the rest of
                    your account data, if you delete your account — see Section 6.3.
                  </p>
                </section>

                {/* Information Sharing */}
                <section id="information-sharing" className={styles.section}>
                  <h2 className={styles.sectionTitle}>4. Information Sharing and Disclosure</h2>
                  <p className={styles.paragraph}>
                    We may share your information in the following circumstances:
                  </p>

                  <h3 className={styles.subsectionTitle}>4.1 With Your Consent</h3>
                  <p className={styles.paragraph}>
                    We will share your information when you explicitly authorize us to do so.
                  </p>

                  <h3 className={styles.subsectionTitle}>4.2 Service Providers</h3>
                  <p className={styles.paragraph}>
                    We may share information with third-party service providers who perform services 
                    on our behalf, such as hosting, analytics, email delivery, and customer support.
                  </p>

                  <h3 className={styles.subsectionTitle}>4.3 Public Information</h3>
                  <p className={styles.paragraph}>
                    Information you choose to make public, such as reviews, ratings, and profile 
                    information, will be visible to other users of our platform.
                  </p>

                  <h3 className={styles.subsectionTitle}>4.4 Legal Requirements</h3>
                  <p className={styles.paragraph}>
                    We may disclose your information if required by law or in response to valid 
                    legal processes, such as subpoenas or court orders.
                  </p>

                  <h3 className={styles.subsectionTitle}>4.5 Business Transfers</h3>
                  <p className={styles.paragraph}>
                    In the event of a merger, acquisition, or sale of assets, your information may 
                    be transferred to the acquiring entity.
                  </p>
                </section>

                {/* Data Security */}
                <section id="data-security" className={styles.section}>
                  <h2 className={styles.sectionTitle}>5. Data Security</h2>
                  <p className={styles.paragraph}>
                    We implement appropriate technical and organizational security measures to protect 
                    your personal information against unauthorized access, alteration, disclosure, or 
                    destruction. These measures include:
                  </p>
                  <ul className={styles.list}>
                    <li>Encryption of data in transit and at rest</li>
                    <li>Regular security assessments and updates</li>
                    <li>Access controls and authentication mechanisms</li>
                    <li>Employee training on data protection</li>
                    <li>Incident response procedures</li>
                  </ul>
                  <p className={styles.paragraph}>
                    However, no method of transmission over the internet or electronic storage is 
                    100% secure. While we strive to protect your information, we cannot guarantee 
                    absolute security.
                  </p>
                </section>

                {/* User Rights */}
                <section id="user-rights" className={styles.section}>
                  <h2 className={styles.sectionTitle}>6. Your Rights and Choices</h2>
                  <p className={styles.paragraph}>
                    You have the following rights regarding your personal information:
                  </p>

                  <h3 className={styles.subsectionTitle}>6.1 Access and Portability</h3>
                  <p className={styles.paragraph}>
                    You can request a copy of your personal information in a portable format.
                  </p>

                  <h3 className={styles.subsectionTitle}>6.2 Correction</h3>
                  <p className={styles.paragraph}>
                    You can update or correct your account information at any time through your 
                    profile settings.
                  </p>

                  <h3 className={styles.subsectionTitle}>6.3 Deletion</h3>
                  <p className={styles.paragraph}>
                    You can request deletion of your account and personal information, subject to 
                    certain legal obligations and legitimate business interests. Account deletion
                    also permanently removes every product-matching training record linked to your
                    pseudonymous identifier (see Section 3.1), not just your visible account data.
                  </p>

                  <h3 className={styles.subsectionTitle}>6.4 Opt-Out</h3>
                  <p className={styles.paragraph}>
                    You can opt out of marketing communications by following the unsubscribe 
                    instructions in our emails or adjusting your account settings.
                  </p>

                  <h3 className={styles.subsectionTitle}>6.5 Object to Processing</h3>
                  <p className={styles.paragraph}>
                    You can object to certain types of data processing, such as processing for 
                    direct marketing purposes.
                  </p>
                </section>

                {/* Cookies */}
                <section id="cookies" className={styles.section}>
                  <h2 className={styles.sectionTitle}>7. Cookies and Tracking Technologies</h2>
                  <p className={styles.paragraph}>
                    We use cookies and similar tracking technologies to collect and store information 
                    about your interactions with our platform. Cookies help us:
                  </p>
                  <ul className={styles.list}>
                    <li>Remember your preferences and settings</li>
                    <li>Understand how you use our services</li>
                    <li>Improve your user experience</li>
                    <li>Provide personalized content and recommendations</li>
                  </ul>
                  <p className={styles.paragraph}>
                    You can control cookies through your browser settings. However, disabling cookies 
                    may affect the functionality of our services.
                  </p>
                </section>

                {/* Third-Party Services */}
                <section id="third-party" className={styles.section}>
                  <h2 className={styles.sectionTitle}>8. Third-Party Services</h2>
                  <p className={styles.paragraph}>
                    Our platform may contain links to third-party websites and services. We are not 
                    responsible for the privacy practices of these third parties. We encourage you to 
                    review their privacy policies before providing any personal information.
                  </p>
                  <p className={styles.paragraph}>
                    We may use third-party analytics and advertising services, which may collect 
                    information about your online activities across different websites and services.
                  </p>
                </section>

                {/* Children's Privacy */}
                <section id="childrens-privacy" className={styles.section}>
                  <h2 className={styles.sectionTitle}>9. Children's Privacy</h2>
                  <p className={styles.paragraph}>
                    Our services are not intended for children under the age of 13. We do not 
                    knowingly collect personal information from children under 13. If we become aware 
                    that we have collected information from a child under 13, we will take steps to 
                    delete such information promptly.
                  </p>
                  <p className={styles.paragraph}>
                    If you are a parent or guardian and believe your child has provided us with 
                    personal information, please contact us immediately.
                  </p>
                </section>

                {/* Policy Updates */}
                <section id="policy-updates" className={styles.section}>
                  <h2 className={styles.sectionTitle}>10. Changes to This Privacy Policy</h2>
                  <p className={styles.paragraph}>
                    We may update this Privacy Policy from time to time to reflect changes in our 
                    practices, technology, legal requirements, or other factors. We will notify you of 
                    any material changes by:
                  </p>
                  <ul className={styles.list}>
                    <li>Posting the updated policy on our website</li>
                    <li>Updating the "Last Updated" date at the top of this policy</li>
                    <li>Sending you an email notification (for significant changes)</li>
                  </ul>
                  <p className={styles.paragraph}>
                    Your continued use of our services after the effective date of the updated policy 
                    constitutes your acceptance of the changes.
                  </p>
                </section>

                {/* Contact */}
                <section id="contact" className={styles.section}>
                  <h2 className={styles.sectionTitle}>11. Contact Us</h2>
                  <p className={styles.paragraph}>
                    If you have any questions, concerns, or requests regarding this Privacy Policy or 
                    our privacy practices, please contact us:
                  </p>
                  <div className={styles.contactInfo}>
                    <p><strong>Email:</strong> privacy@simpary.com</p>
                    <p><strong>Contact Form:</strong> <a href="/contact">simpary.com/contact</a></p>
                  </div>
                  <p className={styles.paragraph}>
                    We will respond to your inquiry as soon as possible, typically within 30 days.
                  </p>
                </section>
              </div>
            </div>
          </div>
        </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
